import React, { useState, useEffect } from 'react';
import { useApi } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { 
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue 
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle
} from '../components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../components/ui/table';
import { Textarea } from '../components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { 
  ClipboardCheck, Plus, Trophy, TrendingUp, Star, AlertCircle, ChevronRight
} from 'lucide-react';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, Legend
} from 'recharts';
import { toast } from 'sonner';

const CLASSIFICACAO_COLORS = {
  "Excelente": "bg-green-500",
  "Bom": "bg-blue-500",
  "Satisfatório": "bg-yellow-500",
  "Necessita Melhoria": "bg-orange-500",
  "Crítico": "bg-red-500"
};

export default function AvaliacoesPage() {
  const api = useApi();
  const [avaliacoes, setAvaliacoes] = useState([]);
  const [ranking, setRanking] = useState([]);
  const [dimensoes, setDimensoes] = useState([]);
  const [criterios, setCriterios] = useState([]);
  const [instituicoes, setInstituicoes] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [selectedAvaliacao, setSelectedAvaliacao] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  
  // New evaluation form
  const [newEval, setNewEval] = useState({
    instituicao_id: '',
    ano_referencia: new Date().getFullYear(),
    tipo_avaliacao: 'Anual'
  });
  
  // Evaluation form answers
  const [respostas, setRespostas] = useState({});

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { 'Authorization': `Bearer ${token}` };
      
      const [avRes, rankRes, dimRes, critRes, instData] = await Promise.all([
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/avaliacoes`, { headers }),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/avaliacoes/ranking/instituicoes`, { headers }),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/avaliacoes/dimensoes`, { headers }),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/avaliacoes/criterios`, { headers }),
        api.getInstituicoes()
      ]);
      
      // Parse responses with fallbacks for missing endpoints
      const avData = avRes.ok ? await avRes.json() : [];
      const rankData = rankRes.ok ? await rankRes.json() : [];
      const dimData = dimRes.ok ? await dimRes.json() : [];
      const critData = critRes.ok ? await critRes.json() : [];
      
      setAvaliacoes(Array.isArray(avData) ? avData : []);
      setRanking(Array.isArray(rankData) ? rankData : []);
      setDimensoes(Array.isArray(dimData) ? dimData : []);
      setCriterios(Array.isArray(critData) ? critData : []);
      setInstituicoes(instData || []);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAvaliacao = async () => {
    if (!newEval.instituicao_id) {
      toast.error('Selecione uma instituição');
      return;
    }
    
    setFormLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/avaliacoes`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(newEval)
      });
      
      if (!response.ok) throw new Error('Erro ao criar avaliação');
      
      const data = await response.json();
      toast.success('Avaliação criada com sucesso!');
      setModalOpen(false);
      setSelectedAvaliacao(data);
      setFormModalOpen(true);
      loadData();
    } catch (error) {
      console.error('Error:', error);
      toast.error('Erro ao criar avaliação');
    } finally {
      setFormLoading(false);
    }
  };

  const handleSubmitRespostas = async () => {
    if (!selectedAvaliacao) return;
    
    const respostasArray = Object.entries(respostas).map(([criterio_id, value]) => ({
      criterio_id,
      resposta_valor: value
    }));
    
    setFormLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/avaliacoes/${selectedAvaliacao.id}/respostas`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(respostasArray)
      });
      
      if (!response.ok) throw new Error('Erro ao submeter respostas');
      
      toast.success('Avaliação submetida com sucesso!');
      setFormModalOpen(false);
      setRespostas({});
      loadData();
    } catch (error) {
      console.error('Error:', error);
      toast.error('Erro ao submeter avaliação');
    } finally {
      setFormLoading(false);
    }
  };

  const openFormForAvaliacao = (av) => {
    setSelectedAvaliacao(av);
    // Pre-fill existing responses
    const existingRespostas = {};
    (av.respostas || []).forEach(r => {
      existingRespostas[r.criterio_id] = r.resposta_valor;
    });
    setRespostas(existingRespostas);
    setFormModalOpen(true);
  };

  // Prepare radar chart data
  const radarData = dimensoes.map(dim => ({
    dimension: dim.nome.replace(' ', '\n'),
    score: ranking[0]?.score_final ? 
      (selectedAvaliacao?.scores_por_dimensao?.[dim.id] || 0) : 0
  }));

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96" data-testid="avaliacoes-loading">
        <div className="w-8 h-8 border-2 border-slate-200 border-t-[#0066CC] rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="avaliacoes-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Avaliação de Instituições</h1>
          <p className="text-slate-500 mt-1">Sistema de avaliação baseado no framework VISITARE</p>
        </div>
        <Button 
          onClick={() => setModalOpen(true)}
          className="bg-[#0066CC] hover:bg-[#0052a3]"
          data-testid="new-avaliacao-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Nova Avaliação
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card className="card-metric">
          <CardContent className="pt-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500">Total Avaliações</p>
                <p className="text-3xl font-bold text-slate-900 mt-2">{avaliacoes.length}</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-[#0066CC]/10 flex items-center justify-center">
                <ClipboardCheck className="w-6 h-6 text-[#0066CC]" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-metric">
          <CardContent className="pt-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500">Score Médio</p>
                <p className="text-3xl font-bold text-[#00AA66] mt-2">
                  {avaliacoes.length > 0 
                    ? (avaliacoes.reduce((sum, a) => sum + (a.score_final || 0), 0) / avaliacoes.filter(a => a.score_final).length).toFixed(1)
                    : '—'}
                </p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-[#00AA66]" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-metric">
          <CardContent className="pt-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500">Excelentes</p>
                <p className="text-3xl font-bold text-purple-600 mt-2">
                  {avaliacoes.filter(a => a.classificacao === 'Excelente').length}
                </p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center">
                <Star className="w-6 h-6 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-metric">
          <CardContent className="pt-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500">Necessitam Atenção</p>
                <p className="text-3xl font-bold text-red-600 mt-2">
                  {avaliacoes.filter(a => ['Crítico', 'Necessita Melhoria'].includes(a.classificacao)).length}
                </p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-red-100 flex items-center justify-center">
                <AlertCircle className="w-6 h-6 text-red-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Ranking and Dimensions */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Ranking */}
        <Card data-testid="ranking-card">
          <CardHeader>
            <CardTitle className="text-lg font-semibold flex items-center gap-2">
              <Trophy className="w-5 h-5 text-amber-500" />
              Ranking de Instituições
            </CardTitle>
          </CardHeader>
          <CardContent>
            {ranking.length === 0 ? (
              <p className="text-center text-slate-500 py-8">Nenhuma avaliação concluída</p>
            ) : (
              <div className="space-y-3">
                {ranking.slice(0, 5).map((item, index) => (
                  <div key={item.instituicao_id} className="flex items-center gap-4 p-3 bg-slate-50 rounded-lg">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-white ${
                      index === 0 ? 'bg-amber-500' : index === 1 ? 'bg-slate-400' : index === 2 ? 'bg-amber-700' : 'bg-slate-300'
                    }`}>
                      {item.posicao}
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-slate-900">{item.instituicao_nome}</p>
                      <p className="text-xs text-slate-500">{item.ano_referencia}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-lg text-[#0066CC]">{item.score_final}</p>
                      <Badge className={`${CLASSIFICACAO_COLORS[item.classificacao]} text-white text-xs`}>
                        {item.classificacao}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Dimensions */}
        <Card data-testid="dimensoes-card">
          <CardHeader>
            <CardTitle className="text-lg font-semibold">Dimensões de Avaliação</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {dimensoes.map((dim) => (
                <div key={dim.id} className="flex items-center justify-between p-3 border border-slate-100 rounded-lg">
                  <div>
                    <p className="font-medium text-slate-900">{dim.nome}</p>
                    <p className="text-xs text-slate-500">{dim.descricao}</p>
                  </div>
                  <Badge variant="outline" className="text-[#0066CC]">{dim.peso}%</Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Evaluations Table */}
      <Card data-testid="avaliacoes-table">
        <CardHeader>
          <CardTitle className="text-lg font-semibold">Avaliações Realizadas</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead>Instituição</TableHead>
                <TableHead>Ano</TableHead>
                <TableHead>Tipo</TableHead>
                <TableHead className="text-center">Score</TableHead>
                <TableHead>Classificação</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {avaliacoes.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-12 text-slate-500">
                    Nenhuma avaliação registada
                  </TableCell>
                </TableRow>
              ) : (
                avaliacoes.map((av) => (
                  <TableRow key={av.id} className="hover:bg-slate-50">
                    <TableCell className="font-medium">{av.instituicao_nome}</TableCell>
                    <TableCell>{av.ano_referencia}</TableCell>
                    <TableCell><Badge variant="outline">{av.tipo_avaliacao}</Badge></TableCell>
                    <TableCell className="text-center font-bold text-[#0066CC]">
                      {av.score_final ?? '—'}
                    </TableCell>
                    <TableCell>
                      {av.classificacao && (
                        <Badge className={`${CLASSIFICACAO_COLORS[av.classificacao]} text-white`}>
                          {av.classificacao}
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">{av.status}</Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => openFormForAvaliacao(av)}
                      >
                        {av.status === 'Rascunho' ? 'Preencher' : 'Ver'}
                        <ChevronRight className="w-4 h-4 ml-1" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Create Evaluation Modal */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent data-testid="create-avaliacao-modal">
          <DialogHeader>
            <DialogTitle>Nova Avaliação</DialogTitle>
            <DialogDescription>Criar uma nova avaliação para uma instituição</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Instituição *</Label>
              <Select 
                value={newEval.instituicao_id} 
                onValueChange={(v) => setNewEval(prev => ({ ...prev, instituicao_id: v }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecione..." />
                </SelectTrigger>
                <SelectContent>
                  {instituicoes.map(i => (
                    <SelectItem key={i.id} value={i.id}>{i.nome}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Ano de Referência</Label>
                <Input
                  type="number"
                  value={newEval.ano_referencia}
                  onChange={(e) => setNewEval(prev => ({ ...prev, ano_referencia: parseInt(e.target.value) }))}
                />
              </div>
              <div>
                <Label>Tipo de Avaliação</Label>
                <Select 
                  value={newEval.tipo_avaliacao} 
                  onValueChange={(v) => setNewEval(prev => ({ ...prev, tipo_avaliacao: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Anual">Anual</SelectItem>
                    <SelectItem value="Semestral">Semestral</SelectItem>
                    <SelectItem value="Extraordinária">Extraordinária</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setModalOpen(false)}>Cancelar</Button>
            <Button 
              onClick={handleCreateAvaliacao}
              className="bg-[#0066CC] hover:bg-[#0052a3]"
              disabled={formLoading}
            >
              {formLoading ? 'A criar...' : 'Criar e Preencher'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Evaluation Form Modal */}
      <Dialog open={formModalOpen} onOpenChange={setFormModalOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="evaluation-form-modal">
          <DialogHeader>
            <DialogTitle>Formulário de Avaliação</DialogTitle>
            <DialogDescription>
              {selectedAvaliacao?.instituicao_nome} - {selectedAvaliacao?.ano_referencia}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {dimensoes.map((dim) => (
              <div key={dim.id} className="border rounded-lg p-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-lg text-slate-900">{dim.nome}</h3>
                  <Badge className="bg-[#0066CC]">{dim.peso}%</Badge>
                </div>
                <p className="text-sm text-slate-500 mb-4">{dim.descricao}</p>
                
                <div className="space-y-4">
                  {criterios.filter(c => c.dimensao_id === dim.id).map((crit) => (
                    <div key={crit.id} className="bg-slate-50 rounded-lg p-4">
                      <Label className="text-sm font-medium">{crit.questao}</Label>
                      
                      {crit.tipo_resposta === 'escala_5' && (
                        <RadioGroup
                          className="flex gap-4 mt-2"
                          value={String(respostas[crit.id] || '')}
                          onValueChange={(v) => setRespostas(prev => ({ ...prev, [crit.id]: v }))}
                        >
                          {[1, 2, 3, 4, 5].map(n => (
                            <div key={n} className="flex items-center gap-1">
                              <RadioGroupItem value={String(n)} id={`${crit.id}-${n}`} />
                              <Label htmlFor={`${crit.id}-${n}`} className="text-sm">{n}</Label>
                            </div>
                          ))}
                        </RadioGroup>
                      )}
                      
                      {crit.tipo_resposta === 'sim_nao' && (
                        <RadioGroup
                          className="flex gap-6 mt-2"
                          value={String(respostas[crit.id] || '')}
                          onValueChange={(v) => setRespostas(prev => ({ ...prev, [crit.id]: v }))}
                        >
                          <div className="flex items-center gap-2">
                            <RadioGroupItem value="sim" id={`${crit.id}-sim`} />
                            <Label htmlFor={`${crit.id}-sim`}>Sim</Label>
                          </div>
                          <div className="flex items-center gap-2">
                            <RadioGroupItem value="nao" id={`${crit.id}-nao`} />
                            <Label htmlFor={`${crit.id}-nao`}>Não</Label>
                          </div>
                        </RadioGroup>
                      )}
                      
                      {crit.tipo_resposta === 'numerico' && (
                        <Input
                          type="number"
                          className="mt-2 w-32"
                          value={respostas[crit.id] || ''}
                          onChange={(e) => setRespostas(prev => ({ ...prev, [crit.id]: e.target.value }))}
                        />
                      )}
                      
                      {crit.tipo_resposta === 'texto' && (
                        <Textarea
                          className="mt-2"
                          value={respostas[crit.id] || ''}
                          onChange={(e) => setRespostas(prev => ({ ...prev, [crit.id]: e.target.value }))}
                        />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setFormModalOpen(false)}>Cancelar</Button>
            <Button 
              onClick={handleSubmitRespostas}
              className="bg-[#00AA66] hover:bg-[#008855]"
              disabled={formLoading}
            >
              {formLoading ? 'A submeter...' : 'Submeter Avaliação'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
